import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useT } from "@/i18n/i18n";
import { useAuth } from "@/context/AuthContext";
import { Logo, LanguageSwitcher } from "@/components/shared";
import { Menu, X } from "lucide-react";

const NAV = [
  ["/mission", "mission"], ["/how-it-works", "how"], ["/farmer-protection", "forFarmers"],
  ["/institutional-food", "forInstitutions"], ["/rescue-preview", "rescue"],
  ["/polska-kwitnie", "kwitnie"],
  ["/investors", "investors"], ["/contact", "contact"],
];

export default function PublicLayout({ children }) {
  const { t } = useT();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  return (
    <div className="min-h-screen bg-offwhite">
      <div className="zp-topbar h-1.5 w-full" />
      <div className="bg-forest/[0.06] border-b border-subtle">
        <div className="mx-auto max-w-7xl px-4 py-1.5 text-center text-xs text-forest">
          {t("pilotBanner")}
        </div>
      </div>
      <header className="sticky top-0 z-40 border-b border-subtle bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3">
          <Logo />
          <nav className="hidden items-center gap-4 lg:flex" aria-label="Primary">
            {NAV.map(([to, key]) => (
              <Link key={to} to={to} data-testid={`nav-${key}`}
                className={`text-sm font-medium transition-colors hover:text-forest ${loc.pathname === to ? "text-forest" : "text-charcoal"}`}>
                {t(`nav.${key}`)}
              </Link>
            ))}
          </nav>
          <div className="hidden items-center gap-3 lg:flex">
            <LanguageSwitcher light={false} />
            {user ? (
              <Link to="/app" data-testid="nav-app" className="rounded-md bg-forest px-3 py-1.5 text-sm font-semibold text-white hover:bg-forestdark">{t("nav.app")}</Link>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login" className="text-sm font-medium text-charcoal hover:text-forest">{t("nav.login")}</Link>
                <Link to="/register" data-testid="nav-register" className="rounded-md bg-forest px-3 py-1.5 text-sm font-semibold text-white hover:bg-forestdark">{t("cta.registerCrop")}</Link>
              </>
            )}
          </div>
          <button className="lg:hidden" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="mobile-menu-btn">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {open && (
          <div className="border-t border-subtle bg-white px-4 py-3 lg:hidden">
            <div className="mb-3"><LanguageSwitcher /></div>
            <div className="flex flex-col gap-2">
              {NAV.map(([to, key]) => (
                <Link key={to} to={to} onClick={() => setOpen(false)} className="py-1 text-sm font-medium text-charcoal">{t(`nav.${key}`)}</Link>
              ))}
              <Link to="/rescue-preview" onClick={() => setOpen(false)} className="py-1 text-sm font-medium text-charcoal">{t("nav.rescue")}</Link>
              <div className="mt-2 flex gap-2">
                {user ? (
                  <Link to="/app" className="rounded-md bg-forest px-3 py-1.5 text-sm font-semibold text-white">{t("nav.app")}</Link>
                ) : (
                  <>
                    <Link to="/login" className="rounded-md border border-forest px-3 py-1.5 text-sm font-semibold text-forest">{t("nav.login")}</Link>
                    <Link to="/register" className="rounded-md bg-forest px-3 py-1.5 text-sm font-semibold text-white">{t("nav.register")}</Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      <main>{children}</main>

      <footer className="mt-24 bg-forestdark text-white/80">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-12 md:grid-cols-4">
          <div className="md:col-span-2">
            <Logo light />
            <p className="mt-4 max-w-md text-sm">{t("slogan")}</p>
            <p className="mt-2 max-w-md text-xs text-white/50">{t("footer.disclaimer")}</p>
          </div>
          <div>
            <div className="mb-3 text-sm font-semibold uppercase tracking-wide text-white">{t("nav.contact")}</div>
            <div className="flex flex-col gap-2 text-sm">
              <Link to="/contact" className="hover:text-white" data-testid="footer-contact">{t("nav.contact")}</Link>
              <Link to="/investors" className="hover:text-white">{t("nav.investors")}</Link>
              <Link to="/rescue-preview" className="hover:text-white">{t("nav.rescue")}</Link>
              <Link to="/konrad-kwiatkowski" className="hover:text-white" data-testid="footer-founder">{t("nav.founder")}</Link>
            </div>
          </div>
          <div>
            <div className="mb-3 text-sm font-semibold uppercase tracking-wide text-white">{t("footer.legal")}</div>
            <div className="flex flex-col gap-2 text-sm">
              <Link to="/legal/privacy" className="hover:text-white">{t("footer.privacy")}</Link>
              <Link to="/legal/terms" className="hover:text-white">{t("footer.terms")}</Link>
              <Link to="/legal/data" className="hover:text-white">{t("footer.dpn")}</Link>
              <Link to="/legal/cookies" className="hover:text-white">{t("footer.cookies")}</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10 py-4 text-center text-xs text-white/40">
          © {new Date().getFullYear()} Żywiąca Polska · Hussar Holdings LLC
        </div>
      </footer>
    </div>
  );
}
